/**
 * Form validation utilities
 *
 * Provides validation functions for user input
 */

/**
 * Normalize symbol for duplicate detection.
 *
 * Normalizes symbols by:
 * 1. Trimming whitespace
 * 2. Removing everything after "." (exchange suffix like .HK, .SZ)
 * 3. Removing everything after " " (space-separated suffix)
 * 4. Removing leading zeros
 *
 * Examples:
 * - "0333.hk" -> "333"
 * - "999 HK" -> "999"
 * - "AAPL" -> "AAPL"
 * - "000333.SZ" -> "333"
 *
 * @param symbol - Raw symbol to normalize
 * @returns Normalized symbol for comparison
 */
export function normalizeSymbol(symbol: string): string {
  if (!symbol) {
    return '';
  }

  // Trim whitespace
  let normalized = symbol.trim();

  // Remove everything after "." (exchange suffix)
  // e.g., "0333.hk" -> "0333"
  normalized = normalized.split('.')[0];

  // Remove everything after " " (space-separated suffix)
  // e.g., "999 HK" -> "999"
  normalized = normalized.split(' ')[0];

  // Remove leading zeros
  // e.g., "0333" -> "333", "0005" -> "5"
  normalized = normalized.replace(/^0+/, '');

  // Handle edge case: if symbol was all zeros, return "0"
  if (normalized === '') {
    return '0';
  }

  return normalized;
}

/**
 * Validate email format
 * @param email - Email address to validate
 * @returns true if valid, false otherwise
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate password strength
 * @param password - Password to validate
 * @returns Object with isValid boolean and errorMessage if invalid
 */
export function validatePassword(password: string): { isValid: boolean; errorMessage?: string } {
  if (!password) {
    return { isValid: false, errorMessage: 'Password is required' };
  }

  if (password.length < 8) {
    return { isValid: false, errorMessage: 'Password must be at least 8 characters' };
  }

  if (password.length > 100) {
    return { isValid: false, errorMessage: 'Password must be less than 100 characters' };
  }

  return { isValid: true };
}

/**
 * Validate username format
 * @param username - Username to validate
 * @returns Object with isValid boolean and errorMessage if invalid
 */
export function validateUsername(username: string): { isValid: boolean; errorMessage?: string } {
  if (!username) {
    return { isValid: false, errorMessage: 'Username is required' };
  }

  if (username.length < 3) {
    return { isValid: false, errorMessage: 'Username must be at least 3 characters' };
  }

  if (username.length > 255) {
    return { isValid: false, errorMessage: 'Username must be 255 characters or less' };
  }

  // Special characters are allowed (an email address can be the username);
  // whitespace is not: the backend stores one canonical lowercase form.
  if (/\s/.test(username)) {
    return { isValid: false, errorMessage: 'Username cannot contain spaces' };
  }

  return { isValid: true };
}

/**
 * Validate comment text
 * @param comment - Comment text to validate
 * @returns Object with isValid boolean and errorMessage if invalid
 */
export function validateComment(comment: string): { isValid: boolean; errorMessage?: string } {
  if (!comment || comment.trim().length === 0) {
    return { isValid: false, errorMessage: 'Comment cannot be empty' };
  }

  if (comment.length > 2000) {
    return { isValid: false, errorMessage: 'Comment must be less than 2000 characters' };
  }

  return { isValid: true };
}

/**
 * Validate required field
 * @param value - Value to validate
 * @param fieldName - Name of the field for error message
 * @returns Object with isValid boolean and errorMessage if invalid
 */
export function validateRequired(
  value: string,
  fieldName: string
): { isValid: boolean; errorMessage?: string } {
  if (!value || value.trim().length === 0) {
    return { isValid: false, errorMessage: `${fieldName} is required` };
  }

  return { isValid: true };
}

/**
 * Validate stock symbol format
 * @param symbol - Symbol to validate
 * @returns Object with isValid boolean and errorMessage if invalid
 */
export function validateSymbol(symbol: string): { isValid: boolean; errorMessage?: string } {
  if (!symbol || symbol.trim().length === 0) {
    return { isValid: false, errorMessage: 'Symbol is required' };
  }

  if (symbol.length > 20) {
    return { isValid: false, errorMessage: 'Symbol must be 20 characters or less' };
  }

  // Alphanumeric, dots, dashes, underscores only
  const symbolRegex = /^[A-Za-z0-9._-]+$/;
  if (!symbolRegex.test(symbol)) {
    return {
      isValid: false,
      errorMessage: 'Symbol can only contain letters, numbers, dots, dashes, and underscores',
    };
  }

  return { isValid: true };
}

/**
 * validate positive number
 * @param value - Number value to validate (as string)
 * @param fieldName - Name of the field for error message
 * @returns Object with isValid boolean and errorMessage if invalid
 */
export function validatePositiveNumber(
  value: string,
  fieldName: string
): { isValid: boolean; errorMessage?: string } {
  if (!value || value.trim().length === 0) {
    return { isValid: false, errorMessage: `${fieldName} is required` };
  }

  const num = parseFloat(value);
  if (isNaN(num)) {
    return { isValid: false, errorMessage: `${fieldName} must be a valid number` };
  }

  if (num <= 0) {
    return { isValid: false, errorMessage: `${fieldName} must be greater than 0` };
  }

  return { isValid: true };
}

/**
 * validate non-negative number (allows 0)
 * @param value - Number value to validate (as string)
 * @param fieldName - Name of the field for error message
 * @returns Object with isValid boolean and errorMessage if invalid
 */
export function validateNonNegativeNumber(
  value: string,
  fieldName: string
): { isValid: boolean; errorMessage?: string } {
  if (!value || value.trim().length === 0) {
    return { isValid: false, errorMessage: `${fieldName} is required` };
  }

  const num = parseFloat(value);
  if (isNaN(num)) {
    return { isValid: false, errorMessage: `${fieldName} must be a valid number` };
  }

  if (num < 0) {
    return { isValid: false, errorMessage: `${fieldName} must be 0 or greater` };
  }

  return { isValid: true };
}

/**
 * Validate currency code (ISO 4217 format)
 * @param currency - Currency code to validate
 * @returns Object with isValid boolean and errorMessage if invalid
 */
export function validateCurrency(currency: string): { isValid: boolean; errorMessage?: string } {
  if (!currency || currency.trim().length === 0) {
    return { isValid: false, errorMessage: 'Currency is required' };
  }

  // ISO 4217: 3 uppercase letters
  const currencyRegex = /^[A-Z]{3}$/;
  if (!currencyRegex.test(currency)) {
    return {
      isValid: false,
      errorMessage: 'Currency must be a 3-letter ISO code (e.g., USD, EUR)',
    };
  }

  return { isValid: true };
}

/**
 * Validate trade date (not in the future)
 * T059 [US3]: Create trade form validation utility
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Object with isValid boolean and errorMessage if invalid
 */
export function validateTradeDate(dateString: string): { isValid: boolean; errorMessage?: string } {
  if (!dateString || dateString.trim().length === 0) {
    return { isValid: false, errorMessage: 'Trade date is required' };
  }

  // Parse date string (YYYY-MM-DD format)
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to midnight for comparison

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return { isValid: false, errorMessage: 'Invalid date format. Use YYYY-MM-DD' };
  }

  // Check if date is in the future
  if (date > today) {
    return { isValid: false, errorMessage: 'Trade date cannot be in the future' };
  }

  return { isValid: true };
}

/**
 * Validate trade form data
 * T059 [US3]: Create trade form validation utility
 * @param symbol - Trade symbol
 * @param tradeDate - Trade date string
 * @param quantity - Trade quantity (positive number)
 * @param tradePrice - Trade price (positive number)
 * @param currency - Currency code
 * @returns Object with isValid boolean and errorMessage for first invalid field
 */
export function validateTrade(
  symbol: string,
  tradeDate: string,
  quantity: string,
  tradePrice: string,
  currency: string
): { isValid: boolean; field?: string; errorMessage?: string } {
  // Validate symbol
  const symbolValidation = validateSymbol(symbol);
  if (!symbolValidation.isValid) {
    return {
      isValid: false,
      field: 'symbol',
      errorMessage: symbolValidation.errorMessage,
    };
  }

  // Validate trade date
  const tradeDateValidation = validateTradeDate(tradeDate);
  if (!tradeDateValidation.isValid) {
    return {
      isValid: false,
      field: 'tradeDate',
      errorMessage: tradeDateValidation.errorMessage,
    };
  }

  // Validate quantity (must be positive, but can be buy or sell)
  if (!quantity || quantity.trim().length === 0) {
    return {
      isValid: false,
      field: 'quantity',
      errorMessage: 'Quantity is required',
    };
  }

  const qtyNum = parseFloat(quantity);
  if (isNaN(qtyNum) || qtyNum === 0) {
    return {
      isValid: false,
      field: 'quantity',
      errorMessage: 'Quantity must be a non-zero number',
    };
  }

  // Validate trade price
  const priceValidation = validatePositiveNumber(tradePrice, 'Trade price');
  if (!priceValidation.isValid) {
    return {
      isValid: false,
      field: 'tradePrice',
      errorMessage: priceValidation.errorMessage,
    };
  }

  // Validate currency
  const currencyValidation = validateCurrency(currency);
  if (!currencyValidation.isValid) {
    return {
      isValid: false,
      field: 'currency',
      errorMessage: currencyValidation.errorMessage,
    };
  }

  return { isValid: true };
}

/**
 * Validate an extracted position before save.
 * T085 [US5]: Frontend validation for AI-extracted positions.
 *
 * All fields except symbol and quantity are optional from AI response.
 * This function validates required fields and any provided optional fields.
 *
 * @param pos - Extracted position object to validate
 * @returns Error message if invalid, null if valid
 */
export function validateExtractedPosition(pos: any): string | null {
  // Symbol is required
  if (!pos.symbol || pos.symbol.trim() === '') {
    return 'Symbol is required';
  }

  // Quantity is required (can be zero for user correction)
  if (pos.quantity === undefined || pos.quantity === null) {
    return 'Quantity is required';
  }

  if (isNaN(pos.quantity)) {
    return 'Quantity must be a valid number';
  }

  // Validate optional numeric fields if present
  if (pos.cost_price !== undefined && pos.cost_price !== null) {
    if (isNaN(pos.cost_price) || pos.cost_price < 0) {
      return 'Cost price must be a positive number';
    }
  }

  if (pos.close_price !== undefined && pos.close_price !== null) {
    if (isNaN(pos.close_price) || pos.close_price < 0) {
      return 'Close price must be a positive number';
    }
  }

  // Validate date if present (allow any date, just check format)
  if (pos.date) {
    const date = new Date(pos.date);
    if (isNaN(date.getTime())) {
      return 'Date must be a valid date (YYYY-MM-DD)';
    }
  }

  return null; // Valid
}

/**
 * Validate an extracted trade before save.
 * T085 [US5]: Frontend validation for AI-extracted trades.
 *
 * All fields except symbol and quantity are optional from AI response.
 * This function validates required fields and any provided optional fields.
 *
 * @param trade - Extracted trade object to validate
 * @returns Error message if invalid, null if valid
 */
export function validateExtractedTrade(trade: any): string | null {
  // Symbol is required
  if (!trade.symbol || trade.symbol.trim() === '') {
    return 'Symbol is required';
  }

  // Quantity is required (can be zero for user correction)
  if (trade.quantity === undefined || trade.quantity === null) {
    return 'Quantity is required';
  }

  if (isNaN(trade.quantity)) {
    return 'Quantity must be a valid number';
  }

  // Validate price if present
  if (trade.price !== undefined && trade.price !== null) {
    if (isNaN(trade.price) || trade.price < 0) {
      return 'Price must be a positive number';
    }
  }

  // Validate commission fee if present
  if (trade.commission_fee !== undefined && trade.commission_fee !== null) {
    if (isNaN(trade.commission_fee) || trade.commission_fee < 0) {
      return 'Commission fee must be a positive number';
    }
  }

  // Validate close price if present
  if (trade.close_price !== undefined && trade.close_price !== null) {
    if (isNaN(trade.close_price) || trade.close_price < 0) {
      return 'Close price must be a positive number';
    }
  }

  // Validate date if present (allow any date, just check format)
  if (trade.date) {
    const date = new Date(trade.date);
    if (isNaN(date.getTime())) {
      return 'Date must be a valid date (YYYY-MM-DD)';
    }
  }

  return null; // Valid
}

/**
 * Validate all extracted data before save.
 * T085 [US5]: Frontend validation for AI-extracted data.
 *
 * Loops through all positions and trades, validating each one.
 * Returns array of error messages for any invalid records.
 *
 * @param positions - Array of extracted positions
 * @param trades - Array of extracted trades
 * @returns Array of error messages (empty if all valid)
 */
export function validateExtractedData(positions: any[], trades: any[]): string[] {
  const errors: string[] = [];

  positions.forEach((pos, index) => {
    const error = validateExtractedPosition(pos);
    if (error) {
      errors.push(`Position ${index + 1} (${pos.symbol || 'Unknown'}): ${error}`);
    }
  });

  trades.forEach((trade, index) => {
    const error = validateExtractedTrade(trade);
    if (error) {
      errors.push(`Trade ${index + 1} (${trade.symbol || 'Unknown'}): ${error}`);
    }
  });

  return errors;
}
